import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { MISSION_TYPE_REUNION_TYPE } from 'src/app/core/services/constant/constants.service';
import { Employee } from 'src/app/shared/models/employee/employee';
import { Mission } from 'src/app/shared/models/mission/mission';
import { MissionType } from 'src/app/shared/models/mission/mission-type';
import { Solution } from 'src/app/shared/models/solution';
import { DateUtils } from 'src/app/shared/utils/date-utils';

@Component({
	selector: 'app-list-planning-week',
	templateUrl: './list-planning-week.component.html',
	styleUrls: ['./list-planning-week.component.scss']
})
export class ListPlanningWeekComponent implements OnChanges {
	@Input() solution: Solution;
	@Input() startDate: Date;
	@Input() appliedFilter: string;
	@Input() isViewByIntervenant: boolean;
	@Output() openEventModalEmitter = new EventEmitter();

	// Données formatées utilisées pour l'affichage du planning en tableau-listing (vue semaine)
	dataWeekViewListing: {
		id: number;
		identity: string;
		missions: any;
	}[] = [];

	// Pour les couleurs des cases
	colorsPastel: any = [
		{ secondary: '#ed7387', primary: '#262c5e' },
		{ secondary: '#484e7f', primary: '#262c5e' },
		{ secondary: '#4c416d', primary: '#262c5e' },
		{ secondary: '#5b6392', primary: '#262c5e' },
		{ secondary: '#6b94c8', primary: '#262c5e' },
		{ secondary: '#6b537a', primary: '#262c5e' },
		{ secondary: '#7078a6', primary: '#262c5e' },
		{ secondary: '#79abc9', primary: '#262c5e' },
		{ secondary: '#805f82', primary: '#262c5e' },
		{ secondary: '#8890be', primary: '#262c5e' },
		{ secondary: '#84beca', primary: '#262c5e' },
		{ secondary: '#9e708e', primary: '#262c5e' },
		{ secondary: '#b9aacd', primary: '#262c5e' },
		{ secondary: '#91cbca', primary: '#262c5e' },
		{ secondary: '#ba7f99', primary: '#262c5e' },
		{ secondary: '#9fb1d6', primary: '#262c5e' },
		{ secondary: '#c7a7c6', primary: '#262c5e' },
		{ secondary: '#a4c2c5', primary: '#262c5e' },
		{ secondary: '#d890a5', primary: '#262c5e' },
		{ secondary: '#98bcd2', primary: '#262c5e' },
		{ secondary: '#dca2ba', primary: '#262c5e' },
		{ secondary: '#bfb5bd', primary: '#262c5e' },
		{ secondary: '#92c6ce', primary: '#262c5e' },
		{ secondary: '#d8aab6', primary: '#262c5e' },
		{ secondary: '#f096a8', primary: '#262c5e' }
	];

	dayOfWeekList: string[] = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];

	constructor(private dateUtils: DateUtils) {}

	/**
	 * Si la solution a changé => recharge des données ; sinon : changement de données : intervenants <-> bénéficiaires
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes.solution || changes.isViewByIntervenant) {
			if (
				changes.solution?.currentValue !== changes.solution?.previousValue ||
				changes.isViewByIntervenant?.currentValue !==
					changes.isViewByIntervenant?.previousValue
			) {
				let missionsToDisplay: Mission[] = this.solution.plannings
					.flatMap((planning) => planning.routes)
					.flatMap((routes) => routes.missions);

				this.dataWeekViewListing = [];

				const viewTypeByIntervenant = changes.isViewByIntervenant
					? changes.isViewByIntervenant?.currentValue
					: this.isViewByIntervenant;
				if (viewTypeByIntervenant) {
					this.dataWeekViewListing =
						this.getDataStructureForListingWeekIntervenant(missionsToDisplay);
				} else {
					missionsToDisplay = missionsToDisplay.concat(
						this.solution.unassignedMissions
					);
					this.dataWeekViewListing =
						this.getDataStructureForListingWeekBeneficiaire(missionsToDisplay);
				}

				for (let i = 0; i < this.dataWeekViewListing.length; i++) {
					this.dataWeekViewListing[i].missions =
						this.getDataStructureForMissionOfListingWeek(
							this.dataWeekViewListing[i].missions
						);
				}
			}
		}
	}

	getColorMission(mission: Mission): string {
		return !this.isMissionInGroup(mission) &&
			!this.isMissionReunion(mission) &&
			this.isMissionAssigned(mission) &&
			this.colorsPastel[mission.assignedEmployee.colorIndex]
			? this.colorsPastel[mission.assignedEmployee.colorIndex].secondary
			: this.isMissionInGroup(mission)
			? 'grey'
			: '#9196B3';
	}

	getDataStructureForListingWeekIntervenant(
		assignedMission: Mission[]
	): { id: number; identity: string; missions: Mission[] }[] {
		return assignedMission.reduce((currentGroupMap, assignedMission) => {
			const employee = assignedMission.assignedEmployee;
			let mapIndex: number;

			const currentGroupMapHasIntervenant = currentGroupMap.some((obj, i) => {
				if (obj && obj.id === employee.id) {
					mapIndex = employee.id;
					return true;
				}
				return false;
			});

			if (!currentGroupMapHasIntervenant) {
				const mission = {
					id: employee.id,
					identity: employee.firstName + ' ' + employee.lastName,
					missions: [assignedMission]
				};
				currentGroupMap.push(mission);
			} else {
				const mission: { id: number; missions: Mission[] } =
					currentGroupMap.find(
						(groupOfMissions) => groupOfMissions.id === mapIndex
					);

				mission.missions.push(assignedMission);
			}

			return currentGroupMap;
		}, []);
	}

	/**
	 * Le 1er reduce permet de ne garder qu'une UNIQUE mission à afficher pour les missions groupées, en ajoutant manuellement
	 * des "attendees" dans la structure de la mission unique qu'on garde
	 * Le 2ème reduce gère la génération de la structure de données pour l'affichage
	 * @param assignedMissions
	 * @returns
	 */
	getDataStructureForListingWeekBeneficiaire(
		assignedMissions: Mission[]
	): { id: number; identity: string; missions: Mission[] }[] {
		return assignedMissions
			.reduce((missionList, mission: Mission) => {
				const missionFromList: Mission = missionList.find(
					({ missionRequest }) =>
						mission.missionRequest.id === missionRequest.id
				);
				if (!missionFromList) {
					missionList.push(mission);
				}
				return missionList;
			}, [])
			.filter((assignedMission: Mission) => {
				return (
					assignedMission.missionRequest.missionType.type !==
					MISSION_TYPE_REUNION_TYPE
				);
			})
			.reduce((currentGroupMap, assignedMission) => {
				const missionRequest = assignedMission.missionRequest;
				let mapIndex: number;

				const currentGroupMapHasBeneficiaire = currentGroupMap.some(
					(obj, i) => {
						if (obj && obj.id === missionRequest.beneficiaire.id) {
							mapIndex = missionRequest.beneficiaire.id;
							return true;
						}
						return false;
					}
				);

				if (!currentGroupMapHasBeneficiaire) {
					const mission = {
						id: missionRequest.beneficiaire.id,
						identity:
							missionRequest.beneficiaire.firstName +
							' ' +
							missionRequest.beneficiaire.lastName,
						missions: [assignedMission]
					};
					currentGroupMap.push(mission);
				} else {
					const mission: { id: number; missions: Mission[] } =
						currentGroupMap.find(
							(groupOfMissions) => groupOfMissions.id === mapIndex
						);

					mission.missions.push(assignedMission);
				}

				return currentGroupMap;
			}, []);
	}

	/**
	 * Sachant que : 1 = lundi, 2 = mardi...
	 * ON veut obtenir le format : { 1: [[missionMatin], [missionAprèm]], 2: [[missionMatin], [missionAprèm]]... }
	 */
	getDataStructureForMissionOfListingWeek(missions: Mission[]): any[] {
		const finalMap = [
			[[], []],
			[[], []],
			[[], []],
			[[], []],
			[[], []]
		];

		for (const mission of missions) {
			const dateOfMission: Date = new Date(mission.missionRequest.date);
			const dayIndex = dateOfMission.getDay() - 1;
			const isEvening = mission.missionRequest.readyTime > 12 * 60 ? 1 : 0;
			finalMap[dayIndex][isEvening].push(mission);

			// Réordonnement chronologiquement les missions à chaque push
			finalMap[dayIndex][isEvening].sort(
				(mission1, mission2) => mission1.realStart - mission2.realStart
			);
		}

		return finalMap;
	}

	openEventModal(missionId: number) {
		this.openEventModalEmitter.emit(missionId);
	}

	getFormattedDate(dayName: string) {
		const dateCopy = new Date(this.startDate);
		return this.dateUtils.addDays(
			dateCopy,
			this.dayOfWeekList.indexOf(dayName)
		);
	}

	getTitleForMission(mission: Mission) {
		const missionTravelTime = mission.realTravelEnd - mission.realTravelStart;
		const realStartValue = mission.realStart;
		const startHour = Math.floor(realStartValue / 60);
		const startMinutes = Math.round(realStartValue % 60);
		const realEndValue = mission.realEnd;
		const endHour = Math.floor(realEndValue / 60);
		const endMinutes = Math.round(realEndValue % 60);

		let title: string = '';
		title += `Trajet :  ${missionTravelTime}min\n`;
		title += `Début :  ${startHour}h${
			startMinutes > 0 ? startMinutes : '00'
		}\n`;
		title += `Fin :  ${endHour}h${endMinutes > 0 ? endMinutes : '00'}`;
		return title;
	}

	isMissionReunion(mission: Mission) {
		return (
			mission.missionRequest.missionType.type === MISSION_TYPE_REUNION_TYPE
		);
	}

	isMissionInGroup(mission: Mission) {
		return mission.missionRequest.nombreIntervenant > 1;
	}

	isMissionAssigned(mission: Mission) {
		return mission.assignedEmployee ? true : false;
	}
}
